import React from 'react';
import { motion } from 'framer-motion';
import { projects } from '../data/projects';
import { useTranslation } from 'react-i18next';

const ACCENTS = [
  {
    text: 'text-arcade-cyan',
    border: 'border-arcade-cyan',
    glow: 'hover:shadow-[0_0_18px_rgba(77,232,244,0.35)]',
  },
  {
    text: 'text-arcade-pink',
    border: 'border-arcade-pink',
    glow: 'hover:shadow-[0_0_18px_rgba(255,46,126,0.35)]',
  },
  {
    text: 'text-arcade-yellow',
    border: 'border-arcade-yellow',
    glow: 'hover:shadow-[0_0_18px_rgba(255,201,61,0.35)]',
  },
];

function Projects() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const localized = (value) =>
    typeof value === 'object' ? value[currentLang] || value.en || value.tr : value;

  // Seviye sıralaması: eskiden yeniye
  const levelProjects = [...projects].sort(
    (a, b) => new Date(a.date || '2000-01-01') - new Date(b.date || '2000-01-01')
  );

  const years = levelProjects
    .filter((p) => p.date)
    .map((p) => new Date(p.date).getFullYear());
  const yearRange =
    years.length > 0 ? `${Math.min(...years)}-${Math.max(...years)}` : '';

  return (
    <section
      id="projects"
      className="min-h-screen relative overflow-hidden bg-arcade-bg crt-scanlines py-16 md:py-20"
    >
      {/* faint grid */}
      <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-[0.06] pointer-events-none"></div>

      {/* decorative pixels */}
      <div className="absolute top-[8%] right-[12%] w-3 h-3 bg-arcade-green"></div>
      <div className="absolute bottom-[10%] left-[7%] w-2.5 h-2.5 bg-arcade-pink"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* section header */}
        <motion.div
          className="flex flex-wrap items-baseline gap-4 md:gap-6 mb-10 md:mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="font-pixel text-arcade-cyan text-2xl md:text-5xl drop-shadow-[0_0_12px_rgba(77,232,244,0.45)]">
            {t('projects.selectLevel')}
          </h2>
          <p className="font-mono text-arcade-purple text-xs md:text-base tracking-widest">
            {levelProjects.length} {t('projects.projectsWord')} · {yearRange}
          </p>
        </motion.div>

        {/* level grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 md:gap-6">
          {levelProjects.map((project, index) => {
            const accent = ACCENTS[Math.floor(index / 4) % ACCENTS.length];
            const year = project.date ? new Date(project.date).getFullYear() : '';
            const engine = project.technologies?.[0] || '';

            return (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (index % 4) * 0.08 }}
                onClick={() => {
                  if (!project.noDetail) window.location.href = `/project/${project.id}`;
                }}
                className={`group ${project.noDetail ? 'cursor-default' : 'cursor-pointer'} border-2 ${accent.border} ${accent.glow} rounded-lg bg-arcade-panel/60 overflow-hidden transition-all duration-200 hover:-translate-y-1 flex flex-col`}
              >
                {/* card header */}
                <div className="flex items-center justify-between px-4 pt-3 pb-2">
                  <span className={`font-pixel ${accent.text} text-sm md:text-base`}>
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  {project.upcoming ? (
                    <span className="font-mono text-[10px] md:text-xs text-arcade-yellow tracking-wider">
                      ■ {project.statusLabel}
                    </span>
                  ) : (
                    <span className="font-mono text-[10px] md:text-xs text-arcade-green tracking-wider">
                      ● {t('projects.cleared')}
                    </span>
                  )}
                </div>

                {/* image */}
                <div className="relative h-32 md:h-36 mx-4 mb-3 overflow-hidden rounded border border-white/5 bg-arcade-bg">
                  <img
                    src={project.image}
                    alt={localized(project.title)}
                    className="w-full h-full object-cover object-center opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.onerror = null;
                    }}
                  />
                </div>

                {/* card footer */}
                <div className="px-4 pb-4 mt-auto">
                  <h3 className="font-mono font-bold text-white text-sm md:text-base tracking-wider uppercase truncate mb-1">
                    {localized(project.title)}
                  </h3>
                  <p className="font-mono text-[11px] md:text-xs text-arcade-purple tracking-widest uppercase">
                    {engine}{year ? ` · ${year}` : ''}
                  </p>
                </div>
              </motion.div>
            );
          })}

          {/* filler hint cell */}
          {levelProjects.length % 4 !== 0 && (
            <motion.div
              className="hidden xl:flex border border-arcade-purple/25 rounded-lg bg-arcade-panel/30 items-center justify-center px-6"
              style={{ gridColumn: `span ${4 - (levelProjects.length % 4)}` }}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <p className="font-mono text-arcade-purple text-sm tracking-widest">
                ▶ {t('projects.selectHint')}
              </p>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}

export default Projects;
